import { useState, useEffect } from "react";
import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Building,
  FilePlus,
  User,
  ChevronLeft,
  ChevronRight,
  Settings,
  Users,
  ListFilter,
  ShieldAlert,
  FileText,
  PlusCircle,
  BarChart3,
  Settings2,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import DashboardNav from "./DashboardNav";
import { useAuth } from "@/context/AuthContext";
import { useAdminAccess } from "@/hooks/useAdminAccess";
import Navbar from "@/components/Navbar";
import { useListingFormContext } from "@/context/ListingFormContext"; // Import useListingFormContext

const DashboardLayout = () => {
  const [collapsed, setCollapsed] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, isLoading } = useAuth();
  const { isAdmin, isSuperAdmin } = useAdminAccess();
  const { clearAllListingData } = useListingFormContext(); // Use the context

  const isAdminRoute = location.pathname.startsWith("/admin");

  useEffect(() => {
    // Only redirect if not loading and not authenticated
    if (!isLoading && !isAuthenticated) {
      navigate("/login", { state: { from: location.pathname } });
    }
  }, [isAuthenticated, isLoading, navigate, location.pathname]);

  // Don't render anything while checking authentication
  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin w-8 h-8 border-4 border-propertylk-blue border-t-transparent rounded-full"></div>
      </div>
    );
  }

  // Only render dashboard content if authenticated
  if (!isAuthenticated) {
    return null;
  }

  // Set up the navigation links
  const dashboardLinks = [
    { href: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
    { href: "/dashboard/listings", label: "My Listings", icon: Building },
    { href: "/dashboard/add-listing", label: "Add Listing", icon: FilePlus },
    { href: "/dashboard/profile", label: "Profile", icon: User },
  ];

  if (isAdmin) {
    dashboardLinks.push({ href: "/dashboard/inquiries", label: "Inquiries", icon: FileText });
  }

  const adminLinks = [
    { href: "/admin", label: "Admin Dashboard", icon: LayoutDashboard },
    { href: "/admin/listings", label: "All Listings", icon: ListFilter },
    { href: "/admin/users", label: "User Management", icon: Users },
    { href: "/admin/inquiries", label: "Inquiries", icon: FileText },
    {
      href: "/admin/create-listing",
      label: "Add New Listing",
      icon: PlusCircle,
    },
    { href: "/admin/create-user", label: "Add New User", icon: User },
    { href: "/admin/analytics", label: "Analytics", icon: BarChart3 },
    { href: "/admin/settings", label: "System Settings", icon: Settings2 },
  ];

  // Use the appropriate links based on the current route
  const links = isAdminRoute ? adminLinks : dashboardLinks;

  return (
    <div className="flex min-h-screen bg-gray-50 w-full overflow-x-hidden">
      {/* Sidebar */}
      <div
        className={cn(
          "bg-white border-r border-gray-200 transition-all duration-300 flex flex-col h-screen sticky top-0",
          collapsed ? "w-16" : "w-64"
        )}
      >
        <div className="p-4 border-b border-gray-100 flex items-center justify-between">
          {!collapsed && (
            <Link to="/" className="flex items-center">
              <img
                src="/src/assets/images/logo.svg"
                alt="PropertyLK"
                className="h-6"
              />
              <span
                className={cn(
                  "ml-2 font-semibold text-lg",
                  isAdminRoute ? "text-red-600" : "text-propertylk-blue"
                )}
              >
                {isAdminRoute ? "Admin Panel" : "Property Portal"}
              </span>
            </Link>
          )}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setCollapsed(!collapsed)}
            className="ml-auto"
          >
            {collapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
          </Button>
        </div>

        <DashboardNav links={links} collapsed={collapsed} />

        {isAdmin && !isAdminRoute && !collapsed && (
          <div className="mt-auto p-4 border-t border-gray-100">
            <Link
              to="/admin"
              className="flex items-center gap-2 text-sm text-red-600 hover:underline"
            >
              <ShieldAlert size={16} />
              Switch to Admin Panel
            </Link>
          </div>
        )}

        {isAdmin && isAdminRoute && !collapsed && (
          <div className="mt-auto p-4 border-t border-gray-100">
            <Link
              to="/dashboard"
              className="flex items-center gap-2 text-sm text-gray-600 hover:underline"
            >
              <User size={16} />
              Return to User Dashboard
            </Link>
          </div>
        )}
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-auto">
        <div
          className={cn(
            "sticky top-0 z-10 flex items-center justify-between p-4 border-b border-gray-200 bg-white",
            isAdminRoute && "bg-red-50"
          )}
        >
          {isAdminRoute && (
            <div className="flex items-center">
              <Badge variant="destructive" className="mr-2">
                {isSuperAdmin ? "SUPER ADMIN" : "ADMIN"}
              </Badge>
            </div>
          )}

          <div className="flex items-center ml-auto gap-2">
            {isAdminRoute && (
              <Button
                size="sm"
                onClick={() => {
                  clearAllListingData(); // Clear local storage
                  navigate("/admin/create-listing");
                }}
                variant="default"
              >
                <PlusCircle className="mr-2 h-4 w-4" />
                Add Listing
              </Button>
            )}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate("/dashboard/notifications")}
            >
              <User className="h-5 w-5" />
            </Button>
          </div>
        </div>

        <div className="p-6">
          <Outlet />
        </div>
      </div>
    </div>
  );
};

export default DashboardLayout;
